import { useState, useEffect, useRef, useCallback } from "react";
import { getAccount } from "../lib/supabase.js";

const ANIMATIONS = {
  none: "",
  pulse: `@keyframes cfPulse { 0%,100% { transform: scale(1); } 50% { transform: scale(1.08); } }`,
  bounce: `@keyframes cfBounce { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-8px); } }`,
  spin: `@keyframes cfSpin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`,
  wiggle: `@keyframes cfWiggle { 0%,100% { transform: rotate(0deg); } 25% { transform: rotate(-8deg); } 75% { transform: rotate(8deg); } }`,
};
const HOVER_ANIMATIONS = {
  none: {}, spin: { animation: "cfSpin 1s linear infinite" },
  scale: { transform: "scale(1.15)", transition: "transform 0.2s ease" },
  glow: { filter: "drop-shadow(0 0 12px rgba(255,255,255,0.4))", transition: "filter 0.2s ease" },
};

const platformUrl = import.meta.env.VITE_SUPABASE_URL;
const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

export default function ChatWidgetPreview({ config, onExpandChange }) {
  const [chatOpen, setChatOpen] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const [hovering, setHovering] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const account = getAccount();
  const c = config || {};

  const primary = c.primary_color || "#0154a4";
  const gradFrom = c.header_gradient_from || primary;
  const gradTo = c.header_gradient_to || (c.secondary_color || "#013a75");
  const bg = c.background_color || "#ffffff";
  const textColor = c.text_color || "#333333";
  const pos = c.position || "bottom-right";
  const launcherSize = Math.min(c.launcher_size || 60, 70);
  const radius = c.border_radius || 16;
  const anim = c.launcher_animation || "pulse";
  const hoverAnim = c.launcher_hover_animation || "spin";
  const suggested = Array.isArray(c.suggested_questions) ? c.suggested_questions : ["Product recommendations", "Pricing info", "Technical specs"];
  const avatarChar = (c.avatar_text || "AI")[0];
  const grad = `linear-gradient(135deg, ${gradFrom}, ${gradTo})`;

  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, isLoading]);

  // Notify parent of expand state changes
  const toggleExpanded = () => {
    const next = !expanded;
    setExpanded(next);
    onExpandChange?.(next);
  };

  // Reset messages when config changes significantly
  const configKey = `${c.greeting_message}|${c.header_title}|${JSON.stringify(c.suggested_questions)}`;
  useEffect(() => { setMessages([]); }, [configKey]);

  const sessionIdRef = useRef(null);

  // ─── Send message via edge function ────────────────────────────────
  const sendMessage = async (text) => {
    const q = (text || input).trim();
    if (!q || isLoading) return;
    setInput("");
    setMessages((prev) => [...prev, { role: "user", content: q }]);
    setIsLoading(true);

    try {
      const history = messages.filter((m) => m.role === "user" || m.role === "assistant").map((m) => ({ role: m.role, content: m.content }));

      const response = await fetch(`${platformUrl}/functions/v1/chat-ai?account=${account?.slug || ""}`, {
        method: "POST",
        headers: { "Authorization": `Bearer ${anonKey}`, "Content-Type": "application/json" },
        body: JSON.stringify({
          message: q,
          history,
          configSlug: c.slug || "default",
          session_id: sessionIdRef.current,
          visitor_page_url: window.location.href,
          visitor_user_agent: navigator.userAgent,
        }),
      });

      const data = await response.json();

      if (data.error) throw new Error(data.error);
      if (data.session_id) sessionIdRef.current = data.session_id;

      setMessages((prev) => [
        ...prev,
        ...(data.products?.length > 0 && c.show_product_cards !== false ? [{ role: "products", products: data.products }] : []),
        { role: "assistant", content: data.answer || "I'm having trouble responding right now." },
      ]);
    } catch (err) {
      console.error("Chat error:", err);
      setMessages((prev) => [...prev, { role: "assistant", content: `I'm having trouble connecting right now. Error: ${err.message}` }]);
    }
    setIsLoading(false);
  };

  // ─── Render helpers ────────────────────────────────────────────────
  const Avatar = () => (
    <div style={{ width: 26, height: 26, borderRadius: "50%", background: grad, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 700, flexShrink: 0 }}>{avatarChar}</div>
  );

  const ProductCard = ({ product }) => {
    const p = product;
    return (
      <div onClick={() => p.product_url && window.open(p.product_url, "_blank")} style={{ background: "#fff", border: "1px solid #e8e8e8", borderRadius: 8, overflow: "hidden", cursor: p.product_url ? "pointer" : "default", transition: "box-shadow 0.2s" }} onMouseEnter={(e) => e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.1)"} onMouseLeave={(e) => e.currentTarget.style.boxShadow = "none"}>
        {c.card_show_image !== false && p.image_url && (
          <div style={{ height: expanded ? 120 : 80, overflow: "hidden", background: "#f0f0f0" }}>
            <img src={p.image_url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} onError={(e) => { e.target.parentElement.style.display = "none"; }} />
          </div>
        )}
        <div style={{ padding: 10 }}>
          {p.category && <div style={{ fontSize: 9, color: "#888", textTransform: "uppercase", letterSpacing: "0.5px", fontWeight: 600, marginBottom: 3 }}>{p.category}</div>}
          <div style={{ fontSize: 12, fontWeight: 700, color: textColor, marginBottom: 2, lineHeight: 1.3 }}>{p.name}</div>
          {c.card_show_sku !== false && p.sku && <div style={{ fontSize: 9, color: "#aaa", marginBottom: 4 }}>SKU: {p.sku}</div>}
          {p.short_description && <div style={{ fontSize: 10, color: "#777", marginBottom: 6, lineHeight: 1.4, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>{p.short_description}</div>}
          {c.card_show_features !== false && p.features?.length > 0 && (
            <div style={{ marginBottom: 6 }}>
              {p.features.slice(0, 2).map((f, i) => <div key={i} style={{ fontSize: 9, color: "#666", paddingLeft: 10, position: "relative", marginBottom: 2 }}><span style={{ position: "absolute", left: 0, color: primary }}>✓</span>{f}</div>)}
            </div>
          )}
          {c.card_show_price !== false && p.price && <div style={{ fontSize: 12, fontWeight: 700, color: textColor, marginBottom: 6 }}>{p.price_label || "$"}{p.price}</div>}
          {p.similarity && <div style={{ fontSize: 9, color: primary, fontWeight: 600, marginBottom: 4 }}>{p.similarity}% match</div>}
          <div style={{ padding: "6px 0", background: grad, borderRadius: 5, fontSize: 10, fontWeight: 700, color: "#fff", textAlign: "center" }}>{c.card_cta_text || "View Product →"}</div>
        </div>
      </div>
    );
  };

  const LoadingDots = () => (
    <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
      <Avatar />
      <div style={{ padding: "10px 14px", background: "#fff", border: "1px solid #e8e8e8", borderRadius: "14px 14px 14px 2px" }}>
        <div style={{ display: "flex", gap: 4 }}>
          {[0, 1, 2].map((i) => <div key={i} style={{ width: 7, height: 7, borderRadius: "50%", background: primary, animation: `cfBounce 1.2s ease-in-out ${i * 0.15}s infinite` }} />)}
        </div>
      </div>
    </div>
  );

  const animCSS = Object.values(ANIMATIONS).join("\n");
  const launcherStyle = {
    width: launcherSize, height: launcherSize, borderRadius: "50%",
    background: c.launcher_icon === "custom" && c.launcher_image_url ? "transparent" : grad,
    border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
    color: "#fff", boxShadow: "0 4px 20px rgba(0,0,0,0.2)", padding: 0,
    animation: !hovering && anim !== "none" ? `cf${anim.charAt(0).toUpperCase() + anim.slice(1)} 2s ease-in-out infinite` : undefined,
    ...(hovering ? HOVER_ANIMATIONS[hoverAnim] || {} : {}),
    transition: "transform 0.2s ease, filter 0.2s ease",
  };

  const windowWidth = expanded ? "100%" : `min(${Math.min(c.window_width || 340, 340)}px, calc(100% - 28px))`;
  const windowMaxHeight = expanded ? "100%" : 460;
  const windowPosition = expanded ? { inset: 0, borderRadius: 0 } : { bottom: 14, [pos === "bottom-left" ? "left" : "right"]: 14, borderRadius: radius };

  return (
    <div style={{ position: "relative", minHeight: expanded ? 600 : 520, background: "#f5f5f5", borderRadius: 8, overflow: "hidden" }}>
      <style>{animCSS}</style>

      {/* Fake website background */}
      {!expanded && (
        <div style={{ padding: 20 }}>
          <div style={{ height: 12, width: 180, background: "#ddd", borderRadius: 4, marginBottom: 12 }} />
          <div style={{ height: 8, width: "90%", background: "#e8e8e8", borderRadius: 3, marginBottom: 8 }} />
          <div style={{ height: 8, width: "75%", background: "#e8e8e8", borderRadius: 3, marginBottom: 8 }} />
          <div style={{ height: 80, background: "#e0e0e0", borderRadius: 6, marginBottom: 16 }} />
          <div style={{ height: 8, width: "85%", background: "#e8e8e8", borderRadius: 3, marginBottom: 8 }} />
          <div style={{ height: 8, width: "60%", background: "#e8e8e8", borderRadius: 3 }} />
        </div>
      )}

      {/* Launcher */}
      {!chatOpen && (
        <div style={{ position: "absolute", bottom: 14, [pos === "bottom-left" ? "left" : "right"]: 14, display: "flex", flexDirection: "column", alignItems: "center", gap: 6, zIndex: 5 }}>
          <button onClick={() => { setChatOpen(true); setTimeout(() => inputRef.current?.focus(), 100); }} onMouseEnter={() => setHovering(true)} onMouseLeave={() => setHovering(false)} style={launcherStyle}>
            {c.launcher_icon === "custom" && c.launcher_image_url ? (
              <img src={c.launcher_image_url} alt="" style={{ width: "100%", height: "100%", objectFit: "contain", borderRadius: "50%", ...(hovering && hoverAnim === "spin" ? { animation: "cfSpin 1s linear infinite" } : {}) }} />
            ) : (
              <svg width={launcherSize * 0.35} height={launcherSize * 0.35} viewBox="0 0 24 24" fill="white"><path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2z" /></svg>
            )}
          </button>
          <div style={{ background: "#fff", padding: "4px 12px", borderRadius: 12, fontSize: 10, fontWeight: 600, color: "#333", boxShadow: "0 2px 8px rgba(0,0,0,0.1)", whiteSpace: "nowrap" }}>{c.open_label || "Chat with us"}</div>
        </div>
      )}

      {/* Chat window */}
      {chatOpen && (
        <div style={{ position: "absolute", width: windowWidth, background: bg, boxShadow: expanded ? "none" : "0 10px 40px rgba(0,0,0,0.15)", overflow: "hidden", display: "flex", flexDirection: "column", maxHeight: windowMaxHeight, zIndex: 10, ...windowPosition }}>
          {/* Header */}
          <div style={{ padding: "12px 14px", background: grad, color: "#fff", display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              {c.logo_url ? <img src={c.logo_url} alt="" style={{ height: 24, borderRadius: 4 }} onError={(e) => { e.target.style.display = "none"; }} /> : <div style={{ width: 26, height: 26, borderRadius: 6, background: "rgba(255,255,255,0.2)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 700 }}>{avatarChar}</div>}
              <div><div style={{ fontSize: 12, fontWeight: 700 }}>{c.header_title || "AI Assistant"}</div><div style={{ fontSize: 9, opacity: 0.85 }}>{c.header_subtitle || "Ask me about our products!"}</div></div>
            </div>
            <div style={{ display: "flex", gap: 6, alignItems: "center", flexShrink: 0 }}>
              {c.expand_enabled !== false && (
                <button onClick={toggleExpanded} style={{ width: 24, height: 24, borderRadius: "50%", background: "rgba(255,255,255,0.2)", border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }} title={expanded ? "Collapse" : "Expand"}>
                  {expanded ? (
                    <svg width={12} height={12} viewBox="0 0 24 24" fill="white"><path d="M5 16h3v3h2v-5H5v2zm3-8H5v2h5V5H8v3zm6 11h2v-3h3v-2h-5v5zm2-11V5h-2v5h5V8h-3z" /></svg>
                  ) : (
                    <svg width={12} height={12} viewBox="0 0 24 24" fill="white"><path d="M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z" /></svg>
                  )}
                </button>
              )}
              <button onClick={() => { setChatOpen(false); if (expanded) { setExpanded(false); onExpandChange?.(false); } }} style={{ width: 24, height: 24, borderRadius: "50%", background: "rgba(255,255,255,0.2)", border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <svg width={12} height={12} viewBox="0 0 24 24" fill="white"><path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" /></svg>
              </button>
            </div>
          </div>

          {/* Messages */}
          <div style={{ flex: 1, padding: 12, overflowY: "auto", background: "#f8f9fa", minHeight: expanded ? 400 : 180 }}>
            {/* Greeting */}
            <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
              <Avatar />
              <div style={{ flex: 1 }}>
                <div style={{ padding: "9px 12px", background: "#fff", border: "1px solid #e8e8e8", borderRadius: "12px 12px 12px 2px", fontSize: 11, color: textColor, lineHeight: 1.6 }}>{c.greeting_message || "Hi! How can I help you today?"}</div>
                {messages.length === 0 && (
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 5, marginTop: 6 }}>
                    {suggested.map((q, i) => (
                      <button key={i} onClick={() => sendMessage(q)} style={{ padding: "4px 10px", background: "#fff", border: "1px solid #ddd", borderRadius: 10, fontSize: 9, color: "#555", cursor: "pointer", fontFamily: "inherit" }}>{q}</button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Conversation */}
            {messages.map((msg, i) => {
              if (msg.role === "user") return (
                <div key={i} style={{ display: "flex", justifyContent: "flex-end", marginBottom: 12 }}>
                  <div style={{ padding: "9px 12px", background: grad, borderRadius: "12px 12px 2px 12px", fontSize: 11, color: "#fff", maxWidth: "85%", lineHeight: 1.5 }}>{msg.content}</div>
                </div>
              );
              if (msg.role === "products") return (
                <div key={i} style={{ display: "flex", gap: 8, marginBottom: 12 }}>
                  <Avatar />
                  <div style={{ flex: 1, display: "grid", gridTemplateColumns: expanded && msg.products.length > 1 ? "1fr 1fr" : "1fr", gap: 8 }}>
                    {msg.products.map((p, j) => <ProductCard key={p.id || j} product={p} />)}
                  </div>
                </div>
              );
              if (msg.role === "assistant") return (
                <div key={i} style={{ display: "flex", gap: 8, marginBottom: 12 }}>
                  <Avatar />
                  <div style={{ padding: "9px 12px", background: "#fff", border: "1px solid #e8e8e8", borderRadius: "12px 12px 12px 2px", fontSize: 11, color: textColor, lineHeight: 1.6, maxWidth: "85%", whiteSpace: "pre-wrap" }}>{msg.content}</div>
                </div>
              );
              return null;
            })}

            {isLoading && <LoadingDots />}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div style={{ padding: "8px 12px", borderTop: "1px solid #e8e8e8", background: bg, display: "flex", gap: 6, alignItems: "center", flexShrink: 0 }}>
            <input ref={inputRef} value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && sendMessage()} placeholder={c.placeholder_text || "Ask about products..."} disabled={isLoading} style={{ flex: 1, padding: "7px 12px", border: "1px solid #e0e0e0", borderRadius: 18, fontSize: 11, outline: "none", fontFamily: "inherit", color: textColor, background: "#fff" }} />
            <button onClick={() => sendMessage()} disabled={isLoading || !input.trim()} style={{ width: 30, height: 30, borderRadius: "50%", background: grad, border: "none", cursor: isLoading || !input.trim() ? "not-allowed" : "pointer", display: "flex", alignItems: "center", justifyContent: "center", opacity: isLoading || !input.trim() ? 0.5 : 1, flexShrink: 0 }}>
              <svg width={13} height={13} viewBox="0 0 24 24" fill="white"><path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" /></svg>
            </button>
          </div>

          {c.show_powered_by !== false && (
            <div style={{ padding: "3px 12px", fontSize: 8, color: "#ccc", textAlign: "center", background: bg, flexShrink: 0 }}>{c.powered_by_text || "Powered by Clarifico AI"}</div>
          )}
        </div>
      )}
    </div>
  );
}
